import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { ListFilter, RefreshCw, Download } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { companyAPI, controllingAPI } from '../services/api';

const todayIso = () => new Date().toISOString().slice(0, 10);
const yearStartIso = () => `${new Date().getFullYear()}-01-01`;

const GLLineItems = () => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [searchParams] = useSearchParams();

  const [companyCodes, setCompanyCodes] = useState([]);
  const [glAccounts, setGlAccounts] = useState([]);
  const [costCenters, setCostCenters] = useState([]);

  const [filters, setFilters] = useState({
    company_code_id: searchParams.get('company_code_id') || '',
    fiscal_year: searchParams.get('fiscal_year') || String(new Date().getFullYear()),
    gl_account: searchParams.get('gl_account') || '',
    cost_center_id: searchParams.get('cost_center_id') || '',
    posting_date_from: searchParams.get('posting_date_from') || yearStartIso(),
    posting_date_to: searchParams.get('posting_date_to') || todayIso(),
    include_reversed: searchParams.get('include_reversed') === '1',
  });

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [cc, gl, cs] = await Promise.all([
          companyAPI.getCompanyCodes(),
          axios.get(`${backendUrl}/api/gl-accounts`),
          controllingAPI.getCostCenters(),
        ]);
        setCompanyCodes(cc.data || []);
        setGlAccounts(gl.data || []);
        setCostCenters(cs.data || []);
        if (cc.data?.length) {
          setFilters((f) => ({ ...f, company_code_id: cc.data[0].company_code_id }));
        }
      } catch (e) {
        toast.error('Failed to load master data');
      }
    })();
  }, [backendUrl]);

  const runReport = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === '' || v === false || v === null) return;
        params[k] = v;
      });
      const r = await axios.get(`${backendUrl}/api/fi/reports/gl-line-items`, { params });
      setRows(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report');
    } finally { setLoading(false); }
  }, [backendUrl, filters]);

  // Auto-run once company code is loaded
  useEffect(() => {
    if (filters.company_code_id) runReport();
  }, [filters.company_code_id, runReport]);

  const totals = rows.reduce(
    (a, l) => {
      const amt = Number(l.amount) || 0;
      if (l.debit_credit === 'D') a.debit += amt;
      else a.credit += amt;
      return a;
    },
    { debit: 0, credit: 0 }
  );

  const downloadCsv = () => {
    if (!rows.length) return;
    const headers = [
      'Document', 'Type', 'Posting Date', 'GL', 'D/C', 'Amount', 'Currency',
      'Cost Center', 'Profit Center', 'Business Area', 'Segment', 'Reference', 'Text', 'Status',
    ];
    const lines = rows.map((r) => [
      r.document_number, r.document_type_id, r.posting_date, r.gl_account, r.debit_credit,
      r.amount, r.currency, r.cost_center_id || '', r.profit_center_id || '',
      r.business_area || '', r.segment_id || '', r.reference || '', r.line_text || '', r.status,
    ].map((v) => `"${(v ?? '').toString().replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...lines].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gl-line-items-${filters.company_code_id}-${filters.fiscal_year}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="gl-line-items-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <ListFilter className="w-7 h-7 text-orange-600" />
              GL Line-Item Display (FBL3N)
            </h1>
            <p className="text-gray-600 mt-1">
              Drill into every individual posting line in the General Ledger.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={downloadCsv} disabled={!rows.length} data-testid="gl-export-csv-btn">
              <Download className="w-4 h-4 mr-1" />Export CSV
            </Button>
            <Button onClick={runReport} disabled={loading} data-testid="gl-run-btn">
              <RefreshCw className="w-4 h-4 mr-1" />{loading ? 'Loading…' : 'Run'}
            </Button>
          </div>
        </div>

        <Card className="mb-4">
          <CardHeader><CardTitle>Filters</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-3 gap-3">
            <div>
              <Label>Company Code</Label>
              <Select
                value={filters.company_code_id}
                onValueChange={(v) => setFilters({ ...filters, company_code_id: v })}
              >
                <SelectTrigger data-testid="gl-cc-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  {companyCodes.map((c) => (
                    <SelectItem key={c.company_code_id} value={c.company_code_id}>
                      {c.company_code_id} - {c.company_code_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Fiscal Year</Label>
              <Input
                value={filters.fiscal_year}
                onChange={(e) => setFilters({ ...filters, fiscal_year: e.target.value })}
                data-testid="gl-fy-input"
              />
            </div>
            <div>
              <Label>GL Account</Label>
              <Select
                value={filters.gl_account || 'all'}
                onValueChange={(v) => setFilters({ ...filters, gl_account: v === 'all' ? '' : v })}
              >
                <SelectTrigger data-testid="gl-account-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All GL Accounts</SelectItem>
                  {glAccounts
                    .filter((g) => !filters.company_code_id || g.company_code === filters.company_code_id)
                    .map((g) => (
                      <SelectItem key={`${g.gl_account}-${g.company_code}`} value={g.gl_account}>
                        {g.gl_account} - {g.account_name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Cost Center</Label>
              <Select
                value={filters.cost_center_id || 'all'}
                onValueChange={(v) => setFilters({ ...filters, cost_center_id: v === 'all' ? '' : v })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cost Centers</SelectItem>
                  {costCenters.map((cc) => (
                    <SelectItem key={cc.cost_center_id} value={cc.cost_center_id}>
                      {cc.cost_center_id} - {cc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Posting Date From</Label>
              <Input
                type="date"
                value={filters.posting_date_from}
                onChange={(e) => setFilters({ ...filters, posting_date_from: e.target.value })}
              />
            </div>
            <div>
              <Label>Posting Date To</Label>
              <Input
                type="date"
                value={filters.posting_date_to}
                onChange={(e) => setFilters({ ...filters, posting_date_to: e.target.value })}
              />
            </div>
            <div className="col-span-3 flex items-center gap-2">
              <Checkbox
                checked={filters.include_reversed}
                onCheckedChange={(v) => setFilters({ ...filters, include_reversed: !!v })}
                data-testid="gl-include-reversed"
              />
              <Label>Include reversed documents</Label>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>{rows.length} Line Item(s)</CardTitle>
            <div className="text-sm text-gray-700 flex gap-6">
              <span>Σ Dr <strong className="font-mono">{totals.debit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
              <span>Σ Cr <strong className="font-mono">{totals.credit.toLocaleString(undefined, { minimumFractionDigits: 2 })}</strong></span>
              <span>Δ <strong className={`font-mono ${Math.abs(totals.debit - totals.credit) < 0.01 ? 'text-emerald-600' : 'text-red-600'}`}>
                {(totals.debit - totals.credit).toFixed(2)}
              </strong></span>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="text-center py-10 text-gray-500">No postings match the filters.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Document</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>GL</TableHead>
                      <TableHead>D/C</TableHead>
                      <TableHead>PK</TableHead>
                      <TableHead className="text-right">Amount</TableHead>
                      <TableHead>Curr</TableHead>
                      <TableHead>CC</TableHead>
                      <TableHead>PC</TableHead>
                      <TableHead>BA</TableHead>
                      <TableHead>Seg</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Text</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rows.map((r) => (
                      <TableRow key={`${r.document_number}-${r.line_no}`} data-testid={`gl-row-${r.document_number}-${r.line_no}`}>
                        <TableCell className="font-mono">{r.document_number}</TableCell>
                        <TableCell><Badge variant="outline">{r.document_type_id}</Badge></TableCell>
                        <TableCell>{r.posting_date}</TableCell>
                        <TableCell className="font-medium">{r.gl_account}</TableCell>
                        <TableCell>
                          <Badge variant={r.debit_credit === 'D' ? 'default' : 'secondary'}>
                            {r.debit_credit === 'D' ? 'Dr' : 'Cr'}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-mono">{r.posting_key}</TableCell>
                        <TableCell className="text-right font-mono">
                          {Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                        </TableCell>
                        <TableCell>{r.currency}</TableCell>
                        <TableCell className="text-xs">{r.cost_center_id || '-'}</TableCell>
                        <TableCell className="text-xs">{r.profit_center_id || '-'}</TableCell>
                        <TableCell className="text-xs">{r.business_area || '-'}</TableCell>
                        <TableCell className="text-xs">{r.segment_id || '-'}</TableCell>
                        <TableCell className="text-xs">{r.reference || '-'}</TableCell>
                        <TableCell className="text-xs">{r.line_text || '-'}</TableCell>
                        <TableCell>
                          <span className={`text-xs px-2 py-0.5 rounded ${r.status === 'REVERSED' ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-700'}`}>
                            {r.status}
                          </span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default GLLineItems;
